import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Button, Modal, Select, message, Card, Typography, Input, Row, Col, DatePicker } from 'antd';
import { EditOutlined, SearchOutlined, ReloadOutlined, InboxOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import EmptyState from '../components/EmptyState';

const { Title, Text } = Typography;
const { Option } = Select;

const UsedInventory = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [prevData, setPrevData] = useState([]);
    const [lastValidSearch, setLastValidSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [searchText, setSearchText] = useState('');
    const [emptyReason, setEmptyReason] = useState(null);
    const [emptyResultExpected, setEmptyResultExpected] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setEmptyReason(null);
        setEmptyResultExpected(false);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/buyback/used-items', {
                params: { search: searchText },
                headers: { Authorization: `Bearer ${token}` }
            });

            const results = res.data;

            if (results.length > 0) {
                setPrevData(results);
                setLastValidSearch(searchText);
                setData(results);
            } else {
                setData([]);
                // Rule 1 & 3: Validate logic and provide reason
                if (searchText) {
                    // Check if search term is "likely" to return zero results (Rule 1)
                    // We can also check if the search term is a valid serial number format or something similar
                    // For now, keep it simple but clear.
                    setEmptyReason(`No used items match the search term "${searchText}".`);

                    if (searchText.length > 15) {
                        setEmptyResultExpected(true);
                    }
                } else {
                    setEmptyReason("The used device inventory is currently empty.");
                }
            }
        } catch (error) {
            message.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const revertToLastValid = () => {
        setSearchText(lastValidSearch);
        setData(prevData);
        setEmptyReason(null);
    };

    const handleUpdateStatus = (item) => {
        setSelectedItem(item);
        setNewStatus(item.used_status);
        setIsModalOpen(true);
    };

    const confirmUpdateStatus = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            await axios.patch(`/api/buyback/used-items/${selectedItem.id}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            message.success('Status updated successfully');
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to update status');
        } finally {
            setLoading(false);
        }
    };

    const getStatusTag = (status) => {
        switch (status) {
            case 'purchased': return <Tag color="orange">Purchased</Tag>;
            case 'maintenance': return <Tag color="blue">Under Maintenance</Tag>;
            case 'ready_for_sale': return <Tag color="green">Ready for Sale</Tag>;
            default: return <Tag color="default">{status}</Tag>;
        }
    };

    const columns = [
        { title: 'Asset ID', dataIndex: 'asset_id', key: 'asset_id' },
        { title: 'Serial Number', dataIndex: 'original_serial', key: 'original_serial' },
        { title: 'Model', dataIndex: 'category_model', key: 'category_model' },
        { title: 'Condition', dataIndex: 'condition_grade', key: 'condition_grade', render: (val) => <Tag>Grade {val}</Tag> },
        {
            title: 'Price',
            dataIndex: 'buy_price',
            key: 'buy_price',
            render: (val) => `${parseFloat(val).toFixed(2)} JOD`
        },
        {
            title: 'Status',
            dataIndex: 'used_status',
            key: 'used_status',
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Buy Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button icon={<EditOutlined />} onClick={() => handleUpdateStatus(record)}>Update</Button>
            ),
        },
    ];

    return (
        <Card>
            <Row gutter={[16, 24]} style={{ marginBottom: 20 }}>
                <Col span={12}>
                    <Title level={4}>Used Devices Inventory</Title>
                </Col>
                <Col span={12} style={{ textAlign: 'right', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <Input
                        placeholder="Search by SN or Model"
                        prefix={<SearchOutlined />}
                        style={{ width: 250 }}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        onPressEnter={fetchData}
                    />
                    <Button icon={<ReloadOutlined />} onClick={fetchData} />
                </Col>
            </Row>

            {data.length > 0 ? (
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            ) : (
                <div style={{ padding: '40px 0' }}>
                    <EmptyState
                        message={emptyReason || "No used items found"}
                        description={emptyResultExpected ? "This search is expected to return zero results based on logic." : "Try a different search or revert to the last valid result set."}
                        icon={<InboxOutlined />}
                    />
                    {prevData.length > 0 && (
                        <div style={{ textAlign: 'center', marginTop: -20, marginBottom: 40 }}>
                            <Button type="primary" ghost onClick={revertToLastValid}>
                                Revert to Last Valid Results ({lastValidSearch || 'All'})
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <Modal
                title="Update Device Status"
                open={isModalOpen}
                onOk={confirmUpdateStatus}
                onCancel={() => setIsModalOpen(false)}
                confirmLoading={loading}
            >
                {selectedItem && (
                    <div style={{ padding: '10px 0' }}>
                        <p>Updating status for: <strong>{selectedItem.category_model}</strong> ({selectedItem.original_serial})</p>
                        <div style={{ marginTop: 20 }}>
                            <Text type="secondary">New Status:</Text>
                            <Select
                                value={newStatus}
                                style={{ width: '100%', marginTop: 8 }}
                                onChange={setNewStatus}
                            >
                                <Option value="purchased">Purchased (Awaiting Inspection)</Option>
                                <Option value="maintenance">Under Maintenance / Repair</Option>
                                <Option value="ready_for_sale">Ready for Sale – Used</Option>
                            </Select>
                        </div>
                    </div>
                )}
            </Modal>
        </Card>
    );
};

export default UsedInventory;
